import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import apiService from '../services/api';
import { clearCrmAuthSession, hasStoredCrmAuthToken } from '../utils/authSession';

function readStoredSession(): {
  token: string | null;
  user: Record<string, unknown> | null;
} {
  if (typeof window === 'undefined') {
    return { token: null, user: null };
  }
  if (!hasStoredCrmAuthToken()) {
    return { token: null, user: null };
  }
  let token: string | null = null;
  let user: Record<string, unknown> | null = null;
  try {
    token =
      localStorage.getItem('jwt_token') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('cognito_access_token');
    const raw = localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      user = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    }
  } catch {
    token = null;
    user = null;
  }
  return { token, user };
}

type LoginResult = {
  success: boolean;
  challenge?: 'NEW_PASSWORD_REQUIRED';
  session?: string;
  message?: string;
};

type LoginFn = (email: string, password: string) => Promise<LoginResult>;
type SetNewPasswordFn = (session: string, newPassword: string, email?: string) => Promise<{ success: boolean }>;

export interface CognitoAuthValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  idToken: string | null;
  accessToken: string | null;
  login: LoginFn;
  setNewPassword: SetNewPasswordFn;
  refreshUser: () => Promise<void>;
  logout: () => void;
  getIdToken: () => string | null;
  getAccessToken: () => string | null;
}

const defaultValue: CognitoAuthValue = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  idToken: null,
  accessToken: null,
  login: async () => ({ success: true }),
  setNewPassword: async () => ({ success: true }),
  refreshUser: async () => {},
  logout: () => {},
  getIdToken: () => null,
  getAccessToken: () => null,
};

const CognitoAuthContext = createContext<CognitoAuthValue>(defaultValue);

export function CognitoAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const { token, user: storedUser } = readStoredSession();
    if (token) {
      setIsAuthenticated(true);
      setAccessToken(token);
      setIdToken(token);
      if (storedUser) {
        setUser(storedUser);
      }
    }
    setIsLoading(false);
  }, []);

  const login: LoginFn = async (email: string, password: string) => {
    try {
      const resp = await apiService.login(email, password);
      if (resp?.success) {
        const token =
          resp.token ||
          localStorage.getItem('jwt_token') ||
          localStorage.getItem('access_token');
        setIsAuthenticated(true);
        setUser(resp.user || {});
        if (token) {
          setAccessToken(token);
          setIdToken(token);
        }
        return { success: true };
      }
      // Check if there's a challenge (e.g., NEW_PASSWORD_REQUIRED)
      if (resp?.challenge && resp?.session) {
        return {
          success: false,
          challenge: resp.challenge as 'NEW_PASSWORD_REQUIRED',
          session: resp.session,
          message: resp.message,
        };
      }
      return { success: false, message: resp?.message };
    } catch (e) {
      throw e;
    }
  };

  const setNewPassword: SetNewPasswordFn = async (session: string, newPassword: string, email?: string) => {
    try {
      if (!email) {
        return { success: false };
      }
      const resp = await apiService.respondToAuthChallenge(email, session, newPassword);
      if (resp?.success) {
        const token =
          resp.token ||
          localStorage.getItem('jwt_token') ||
          localStorage.getItem('access_token');
        setIsAuthenticated(true);
        setUser(resp.user || {});
        if (token) {
          setAccessToken(token);
          setIdToken(token);
        }
        return { success: true };
      }
      return { success: false };
    } catch (e) {
      throw e;
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const me = await apiService.getCurrentUser();
      if (me) {
        setUser(me);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !user) {
      refreshUser();
    }
  }, [isLoading, isAuthenticated, user, refreshUser]);

  const logout = () => {
    void apiService.logout();
    clearCrmAuthSession();
    setIsAuthenticated(false);
    setUser(null);
    setIdToken(null);
    setAccessToken(null);
  };

  const value = useMemo<CognitoAuthValue>(() => ({
    isAuthenticated,
    isLoading,
    user,
    idToken,
    accessToken,
    login,
    setNewPassword,
    refreshUser,
    logout,
    getIdToken: () => idToken,
    getAccessToken: () => accessToken,
  }), [isAuthenticated, isLoading, user, idToken, accessToken, refreshUser]);

  return (
    <CognitoAuthContext.Provider value={value}>
      {children}
    </CognitoAuthContext.Provider>
  );
}

export function useCognitoAuth() {
  return useContext(CognitoAuthContext);
}


